/**
 * The ONE onboarding checklist used by both the setup page and the dashboard
 * "your numbers are gross" banner, so they always agree. It's the gate's steps in
 * the founder's chosen order, with Contribution-margin completeness made STRICT
 * (every product costed AND all cost levers set) rather than the gate's loose
 * "≥1 SKU costed" — otherwise the banner would stop nagging while numbers are
 * still gross.
 *
 * Order (founder, 2026-06-23): Account → Connect store → Business costs →
 * Organize catalog → Contribution margin. (Organize before CM — you sort SKUs
 * into products first, then cost them; matches the setup chat's Categorize→Costs.)
 */

import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "./api-context.js";

interface GateStep { step: number; name: string; required: boolean; complete: boolean; detail?: string }
/** Plain-language reason the setup box shows — amber (post-setup new work) vs red
 *  (initial setup unfinished) + counts. Optional so an older API deploy that
 *  doesn't send it just falls back to the red gross-numbers copy. */
export interface SetupReason { incremental: boolean; reviewCount: number; newlySellingCount: number }
interface OnboardingState { completed: boolean; requiredComplete: boolean; finalized: boolean; currentStep: number; steps: GateStep[]; setupReason?: SetupReason | null }
interface CostReadiness { complete: boolean; productsCosted: number; productsTotal: number; items: Array<{ key: string; label: string; done: boolean }> }

export interface ChecklistItem {
  step: number;        // gate step number — the setup page maps this to its action/drawer
  name: string;
  done: boolean;
  required: boolean;   // required to UNLOCK the dashboard (CM + catalog are not)
  detail?: string;
  href: string;        // where the banner sends you to finish it
}

// Where each gate step is finished. Catalog/CM deep-link INTO the right stage of
// the costs chat so the banner CTA (like the setup hub) never restarts an
// already-set-up account at Upload (founder 2026-07-12). ?review=1 fast-forwards
// Categorize straight to the review sheet with the flagged SKU(s) visible.
const STEP_HREF: Record<number, string> = {
  1: "/welcome",          // Account setup
  2: "/welcome",          // Connect store
  4: "/welcome",          // Business costs (overhead drawer)
  6: "/onboarding/chat?stage=categorize&review=1",  // Organize catalog → review sheet
  3: "/onboarding/chat?stage=costs",                // Contribution margin → costs step
};

// THREE phases, matching the /welcome hub (founder 2026-07-06: the banner used
// to list "Business costs / Contribution margin setup / Organize catalog" as
// separate stale-named steps, but onboarding now does catalog + CM inside the
// single costs conversation — so "Calculate your costs" folds all three into
// one line. Done only when business costs + catalog + STRICT CM are all done,
// so the nag stays honest until numbers are truly non-gross.
const GROUPS: Array<{ name: string; steps: number[] }> = [
  { name: "Account setup", steps: [1] },
  { name: "Connect your data", steps: [2] },
  { name: "Calculate your costs", steps: [4, 6, 3] },
];

export interface OnboardingChecklist {
  items: ChecklistItem[];
  complete: boolean;          // every item (incl. strict CM) done
  requiredComplete: boolean;  // dashboard-unlock steps done
  state: OnboardingState | null;
  loading: boolean;
}

export function useOnboardingChecklist(): OnboardingChecklist {
  const api = useApiClient();
  const stateQ = useQuery({ queryKey: ["onboarding", "state"], queryFn: () => api.get<OnboardingState>("/onboarding/state") });
  const readinessQ = useQuery({ queryKey: ["cost-readiness"], queryFn: () => api.get<CostReadiness>("/catalog/cost-readiness"), staleTime: 5 * 60_000 });

  const state = stateQ.data ?? null;
  const loading = stateQ.isLoading;
  const byStep = new Map((state?.steps ?? []).map((s) => [s.step, s]));

  // Per-step done: CM (step 3) uses STRICT cost-readiness; the rest use the gate.
  const stepDone = (step: number): boolean =>
    step === 3 ? Boolean(readinessQ.data?.complete) : Boolean(byStep.get(step)?.complete);

  const items: ChecklistItem[] = GROUPS
    .map(({ name, steps }) => {
      // Only steps the gate actually returned count toward this phase.
      const live = steps.filter((st) => byStep.has(st));
      if (live.length === 0) return null;
      const done = live.every(stepDone);
      const required = live.some((st) => Boolean(byStep.get(st)?.required));
      // Link to the first UNFINISHED sub-step's real action (business costs →
      // hub drawer, catalog/CM → costs chat); fall back to the hub.
      const nextStep = live.find((st) => !stepDone(st)) ?? live[0]!;
      return { step: nextStep, name, done, required, href: STEP_HREF[nextStep] ?? "/welcome" };
    })
    .filter((i): i is ChecklistItem => i !== null);

  const complete = items.length > 0 && items.every((i) => i.done);
  return { items, complete, requiredComplete: Boolean(state?.requiredComplete), state, loading };
}
